import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'

import ModuleContainer from '../../components/module-container'
import Asset from '../../components/cms/asset'
import AspectRatio from '../../components/cms/aspect-ratio'
import Copy from '../../components/cms/copy'
import Heading from '../../components/cms/heading'
import NewsletterForm from '../../components/newsletter-form'
import useNewsletterForm from '../../../hooks/use-newsletter-form'
import {NEWSLETTER_BLOCK_SOURCE} from '../../../hooks/use-newsletter-sign-up'
import {hasValidAsset, hasValidString} from '../../utils/utils'
import {newsletterSlotRecipe} from './index.recipe'

/**
 * Newsletter module: sign-up form with an optional due date beside an image or video.
 *
 * After a successful sign-up the form is replaced in place by the confirmation view
 * (brand background, confirmation heading, copy and image), and focus moves to it.
 *
 * @component
 * @param {Object} props - CMS module properties.
 * @param {number} [props.headingLevel] - HTML heading level of the module heading.
 * @param {Object} props.heading - Sign-up heading; the module renders nothing without `headline1`.
 * @param {Object} [props.copy] - Sign-up description.
 * @param {Object} [props.assetImageVideo] - Image or video shown beside the form.
 * @param {Object} [props.confirmationHeading] - Heading of the confirmation view.
 * @param {Object} [props.confirmationCopy] - Copy of the confirmation view.
 * @param {Object} [props.confirmationAsset] - Image of the confirmation view; falls back to `assetImageVideo`.
 * @param {'default'|'subtle'|'strong'|'inverse'} [props.background] - Module background.
 * @param {string} [props.anchor] - HTML anchor of the module.
 * @param {string} [props.spacing] - Vertical module spacing.
 * @returns {JSX.Element|null}
 */
const Newsletter = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            headingLevel,
            anchor,
            background,
            spacing,
            heading,
            copy,
            assetImageVideo,
            confirmationHeading,
            confirmationCopy,
            confirmationAsset
        },
        ref
    ) => {
        const {form, submitForm, recaptchaRef, isSubscribed, confirmationProps} = useNewsletterForm(
            {
                source: NEWSLETTER_BLOCK_SOURCE
            }
        )
        const view = isSubscribed ? 'confirmation' : 'signUp'
        const recipe = useSlotRecipe({recipe: newsletterSlotRecipe})
        const styles = useMemo(() => recipe({view}), [recipe, view])

        if (!hasValidString(heading?.headline1)) return null

        const asset =
            isSubscribed && hasValidAsset(confirmationAsset) ? confirmationAsset : assetImageVideo

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                spacing={spacing}
                ref={ref}
            >
                <Box css={styles.layout}>
                    <Box css={styles.card}>
                        {isSubscribed ? (
                            <Box {...confirmationProps} css={styles.confirmation}>
                                <Heading
                                    {...confirmationHeading}
                                    variant={
                                        confirmationHeading?.variant === 'hidden'
                                            ? 'hidden'
                                            : 'newsletter'
                                    }
                                    headingLevel={headingLevel}
                                />
                                <Copy {...confirmationCopy} variant="newsletter" />
                            </Box>
                        ) : (
                            <>
                                <Box css={styles.textGroup}>
                                    <Heading {...heading} headingLevel={headingLevel} />
                                    <Copy {...copy} />
                                </Box>
                                <NewsletterForm
                                    form={form}
                                    submitForm={submitForm}
                                    recaptchaRef={recaptchaRef}
                                    layout="condensed"
                                    variant="filled"
                                />
                            </>
                        )}
                    </Box>
                    {hasValidAsset(asset) && (
                        <Box css={styles.media}>
                            <AspectRatio ratio="1/1">
                                <Asset
                                    asset={asset}
                                    objectFit="cover"
                                    widths={{base: '100vw', s: '50vw'}}
                                />
                            </AspectRatio>
                        </Box>
                    )}
                </Box>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Newsletter.displayName = 'Newsletter'
}

const headingShape = PropTypes.shape({
    variant: PropTypes.oneOf(['default', 'small', 'hidden']),
    subline: PropTypes.string,
    headline1: PropTypes.string,
    headline2: PropTypes.string
})

Newsletter.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    headingLevel: PropTypes.number,
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    heading: headingShape,
    copy: PropTypes.object,
    assetImageVideo: PropTypes.object,
    confirmationHeading: headingShape,
    confirmationCopy: PropTypes.object,
    confirmationAsset: PropTypes.object
}

export default Newsletter
